// src/components/pages/shared/Pagination.tsx
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { GLASS_CARD } from '@/components/ui/glass';
import { useTranslation } from 'react-i18next';

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

type PaginationProps = {
  page: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  itemLabel: string; // ej: "servicios", "citas", "clientes"
  className?: string;
  // Opciones del selector "Por página". Por defecto [5,10,20,50]; en listados
  // que se muestran en grid de 3 columnas (p. ej. Explorar empresas) hay que
  // pasar múltiplos de 3 (p. ej. [3,6,9,12]) para que la última fila no
  // quede coja.
  pageSizeOptions?: number[];
};

export default function Pagination({
  page,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  itemLabel,
  className = '',
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}: PaginationProps) {
  const { t } = useTranslation();

  if (totalPages <= 1) return null;

  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  return (
    <motion.div variants={itemVariants} className={`w-full ${className}`}>
      <Card className={`rounded-2xl ${GLASS_CARD}`}>
        <CardContent className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3">
          {/* Conteo + tamaño de página */}
          <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
            <span>
              {startIndex + 1}–{Math.min(endIndex, totalItems)} de{' '}
              <strong className="text-gray-900 dark:text-white">
                {totalItems}
              </strong>{' '}
              {itemLabel}
            </span>
            <span className="hidden sm:block w-px h-4 bg-gray-200 dark:bg-white/10" />
            <div className="hidden sm:flex items-center gap-2">
              <span>{t('common.recordsPerPage', 'Por página')}</span>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => onPageSizeChange(Number(v))}
              >
                <SelectTrigger className="h-8 w-[64px] text-xs rounded-lg border-gray-300 dark:border-white/10 bg-white/70 dark:bg-black/40 focus:ring-1 focus:ring-[#103254] dark:text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[70]">
                  {pageSizeOptions.map((s) => (
                    <SelectItem key={s} value={String(s)} className="text-xs">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Navegación */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page === 1}
              aria-label={t('common.prev', 'Anterior')}
              className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-600 dark:text-gray-300 hover:bg-white/60 dark:hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <span className="min-w-[92px] text-center text-sm font-medium text-[#103254] dark:text-white bg-[#103254]/[0.06] dark:bg-white/5 rounded-lg px-3 py-1.5">
              {t('common.pageOf', 'Página {{page}} de {{total}}', {
                page,
                total: totalPages,
              })}
            </span>

            <button
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              aria-label={t('common.next', 'Siguiente')}
              className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-600 dark:text-gray-300 hover:bg-white/60 dark:hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}